import { create } from 'zustand';
import { api } from '@/lib/api';

interface Task {
  id: string;
  title: string;
  description?: string;
  dueDate?: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TaskState {
  tasks: Task[];
  completedCount: number;
  pendingCount: number;
  showCompleted: boolean;
  isLoading: boolean;
  error: string | null;

  setShowCompleted: (show: boolean) => void;
  fetchTasks: () => Promise<void>;
  createTask: (data: { title: string; description?: string; dueDate?: string }) => Promise<Task | null>;
  updateTask: (id: string, data: { title?: string; description?: string; dueDate?: string | null; completed?: boolean }) => Promise<boolean>;
  deleteTask: (id: string) => Promise<boolean>;
  toggleTask: (id: string) => Promise<boolean>;
  clearError: () => void;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  completedCount: 0,
  pendingCount: 0,
  showCompleted: true,
  isLoading: false,
  error: null,

  setShowCompleted: (show: boolean) => {
    set({ showCompleted: show });
    get().fetchTasks();
  },

  fetchTasks: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.listTasks({ showCompleted: get().showCompleted });
      set({
        tasks: response.tasks,
        completedCount: response.completedCount,
        pendingCount: response.pendingCount,
        isLoading: false,
      });
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
    }
  },

  createTask: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.createTask(data);
      await get().fetchTasks();
      set({ isLoading: false });
      return response.task;
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
      return null;
    }
  },

  updateTask: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      await api.updateTask(id, data);
      await get().fetchTasks();
      set({ isLoading: false });
      return true;
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
      return false;
    }
  },

  deleteTask: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.deleteTask(id);
      await get().fetchTasks();
      set({ isLoading: false });
      return true;
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
      return false;
    }
  },

  toggleTask: async (id) => {
    const task = get().tasks.find((t) => t.id === id);
    if (!task) return false;

    return get().updateTask(id, { completed: !task.completed });
  },

  clearError: () => set({ error: null }),
}));
